import type { ApiResponse } from '@/common/services/api.service-new';
import { mapSettledWithLimit } from '../../helpers/concurrency.helper';
import { InFlightMap } from '../../helpers/in-flight-map.helper';
import { alertFreshnessAt, alertStaleAfter } from './alert-freshness';

/**
 * One zone's answer, as the stores hold it.
 *
 * `refreshedAt` rides on the response, not the payload, because a
 * deforestation `empty` has no payload and still has an age. Subclasses set it;
 * the base stamps `staleAfter` when it caches. An entry without `staleAfter` is
 * stale.
 */
export type ZoneAlertResponse<T> = ApiResponse<T> & {
  refreshedAt?: string | null;
  checkedAt?: string | null;
  staleAfter?: number;
};

/**
 * Shared orchestration for the alerts APIs' batch-by-zone-ids routes.
 *
 * Fire and deforestation both expose `GET /{product}/zones?ids=…`, both cap the
 * id list, and both need the same four things around it: don't ask twice for a
 * zone already cached, don't let concurrent callers stampede the same zone,
 * chunk the id list, and fall back to the per-zone route for whatever the batch
 * could not answer. That is this class. It is deliberately Vue-free and
 * OL-free, so the rules can be unit-tested directly — same reason as
 * `items-pagination.ts` and `arcgis-fetch.ts`.
 *
 * What it does NOT own is the response body. The two products agree on the
 * envelope (`{ zones, max_ids }`) and disagree on everything inside it: fire
 * carries a legacy array form from its rollout, treats an omitted zone as an
 * access boundary rather than an empty result, and has a 204 case. Pushing that
 * through one parameterised parser would produce exactly the flag-soup the repo
 * warns about, so subclasses keep their own `fetchChunk` and `fetchSingular`.
 *
 * Contract: `alerts-batch-by-zone-ids`.
 */
/**
 * What one batch request could answer, and what it could not.
 *
 * `fallbackIds` are deliberately NOT fetched by the subclass: the base collects
 * them across every chunk and runs a single bounded pass at the end. Falling
 * back inside a chunk would serialise batch, fan-out, batch, fan-out, which is
 * worst exactly where it matters most — on the non-default deforestation layers
 * `uncomputed` is the normal answer, not the edge case (contract Delta 3).
 */
export interface ChunkOutcome<T> {
  results: Map<number, ZoneAlertResponse<T>>;
  fallbackIds?: number[];
}

export abstract class ZoneBatchClient<T> {
  private readonly inFlight = new InFlightMap<number, ZoneAlertResponse<T>>();
  private chunkSize: number;

  /**
   * @param cached   Read-through to the caller's store. A non-success or stale
   *                 hit is ignored: a cached failure must not pin a zone to it,
   *                 and neither may a result the backend has since recomputed.
   * @param cache    Write-back, called only for successes, stamped with
   *                 `staleAfter`.
   * @param chunkSize Batch cap. The server echoes its own limit as `max_ids`;
   *                 this is the client-side ceiling and must not exceed it.
   */
  protected constructor(
    private readonly cached: (id: number) => ZoneAlertResponse<T> | null | undefined,
    private readonly cache: (id: number, response: ZoneAlertResponse<T>) => void,
    chunkSize = 200,
  ) {
    this.chunkSize = chunkSize;
  }

  /** How often the product's backend recomputes a zone. See `ALERT_CADENCE_MS`. */
  protected abstract get cadenceMs(): number;

  /**
   * Whether a successful answer is a computed result. One that is not (fire's
   * `uncomputed`) is still stored so the UI can say so, but it goes stale at
   * once, so the next read asks again. Each product answers, because only it
   * knows whether an `uncomputed` can reach the cache at all.
   */
  protected abstract isComputed(response: ZoneAlertResponse<T>): boolean;

  /** The clock, so tests can move it. */
  protected now(): number {
    return Date.now();
  }

  private isFresh(hit: ZoneAlertResponse<T>): boolean {
    return hit.isSuccess && (hit.staleAfter ?? 0) > this.now();
  }

  /**
   * Smallest id count worth spending a batch request on.
   *
   * The batch is cache-only, so whether a small one earns its round trip depends
   * on what the product's *singular* route can answer that the batch cannot —
   * which is the one thing this class deliberately does not know. The default
   * batches whatever it is given; a subclass whose singular route is a strict
   * superset for one id raises it. The two products answer this oppositely, on
   * purpose: see each `fetchSingular`.
   */
  protected get minBatchIds(): number {
    return 1;
  }

  /**
   * Lower the client-side cap to the server's, once it tells us.
   *
   * The cap is env-overridable and echoed as `max_ids`. Raising ours is never
   * safe, so this only ever narrows: a server that reports a larger limit than
   * we ship is still requested at our own ceiling.
   */
  protected noteServerCap(maxIds: unknown): void {
    if (typeof maxIds === 'number' && Number.isInteger(maxIds) && maxIds > 0) {
      this.chunkSize = Math.min(this.chunkSize, maxIds);
    }
  }

  /**
   * The cap named in a 400 refusing the request for exceeding it.
   *
   * The server's limit is env-overridable, so the day it is lowered below ours
   * every chunk is refused — and learning the cap only from a SUCCESSFUL
   * response's `max_ids` means that never self-corrects: the same oversized
   * batch is sent, refused, and sent again on the next page load, forever.
   *
   * The producer pins the wording ("ids list has 250 entries; max is 200") with
   * a test, and the React client parses the same shape. Subclasses call this
   * from `fetchChunk` and defer the chunk to the per-zone fallback, so the user
   * still gets numbers on this pass and the next one chunks to the real cap.
   */
  protected capFromRejection(error: { message?: string; status?: number } | null | undefined): number | undefined {
    if (error?.status !== 400 || typeof error.message !== 'string') {
      return undefined;
    }
    const named = /\bmax is (\d+)\b/.exec(error.message)?.[1];
    if (named === undefined) {
      return undefined;
    }
    const cap = Number(named);
    return Number.isInteger(cap) && cap > 0 ? cap : undefined;
  }

  /**
   * Resolve every id, in the order given, from cache / in-flight / the batch.
   *
   * Duplicate ids collapse to one request and still yield one result each.
   */
  public async getByZoneIds(ids: number[]): Promise<ZoneAlertResponse<T>[]> {
    const pending = new Map<number, Promise<ZoneAlertResponse<T>>>();
    const resolvers = new Map<number, (response: ZoneAlertResponse<T>) => void>();

    for (const id of new Set(ids)) {
      const hit = this.cached(id);
      const existing = this.inFlight.get(id);
      if (hit && this.isFresh(hit)) {
        pending.set(id, Promise.resolve(hit));
      } else if (existing) {
        pending.set(id, existing);
      } else {
        resolvers.set(id, this.inFlight.register(id));
        pending.set(id, this.inFlight.get(id)!);
      }
    }

    // Every miss is registered before the first await, later chunks included,
    // so a concurrent caller joins rather than starting a second batch.
    const misses = [...resolvers.keys()];
    const deferred: number[] = [];
    for (let offset = 0; offset < misses.length; ) {
      const chunk = misses.slice(offset, offset + this.chunkSize);
      // Advance by what this slice actually took, NOT by `this.chunkSize`: the
      // await below can lower the cap (`capFromRejection`), and stepping by the
      // new, smaller cap would re-slice ids the rejected chunk has already
      // handed to the fallback — six zones against a server limit of two send
      // [1..6], then [3,4], then [5,6], then all six singly. The lowered cap
      // still applies from the next slice on, which is the point of learning it.
      offset += chunk.length;
      // Too few ids to be worth the cache-only route: send them to the per-zone
      // one, which for some products answers strictly more. The test is per
      // REQUEST, not per call, so it also catches a short trailing chunk — with
      // 201 misses the second chunk is one id, and that one is the whole point.
      if (chunk.length < this.minBatchIds) {
        deferred.push(...chunk);
        continue;
      }
      let outcome: ChunkOutcome<T>;
      try {
        outcome = await this.fetchChunk(chunk);
      } catch (error) {
        // Contain the failure to this chunk. Letting it escape would leave the
        // remaining chunks' resolvers pending for the life of the page.
        const message = error instanceof Error ? error.message : this.failureMessage;
        outcome = { results: new Map(chunk.map((id) => [id, this.failure(message)])) };
      }
      const pendingFallback = new Set(outcome.fallbackIds ?? []);
      deferred.push(...pendingFallback);
      for (const id of chunk) {
        if (pendingFallback.has(id)) {
          continue;
        }
        this.settle(id, outcome.results.get(id) ?? this.failure(this.missingMessage), resolvers);
      }
    }

    // One bounded pass for everything the batches could not answer, rather than
    // one per chunk.
    if (deferred.length > 0) {
      const results = await this.fallback(deferred);
      for (const id of deferred) {
        this.settle(id, results.get(id) ?? this.failure(this.missingMessage), resolvers);
      }
    }

    return Promise.all(ids.map((id) => pending.get(id)!));
  }

  /**
   * Resolve the waiter, then cache — and never let the cache take the rest down.
   *
   * The cache is the caller's store, and a write into a disposed Pinia store
   * throws. Resolving first is necessary but NOT sufficient: it saves only the
   * id being settled at that instant. The exception still escapes the settle
   * loop and `getByZoneIds`, leaving every id not yet reached registered in the
   * in-flight map against a promise nothing will ever resolve — so a concurrent
   * read for one of those zones joins it and hangs for the life of the page.
   * Containing the write here is what actually makes that impossible.
   *
   * A failed write costs a re-fetch next time. That is the whole cost.
   */
  private settle(
    id: number,
    response: ZoneAlertResponse<T>,
    resolvers: Map<number, (response: ZoneAlertResponse<T>) => void>,
  ): void {
    if (!response.isSuccess) {
      resolvers.get(id)!(response);
      return;
    }
    const entry: ZoneAlertResponse<T> = {
      ...response,
      refreshedAt: response.refreshedAt ?? null,
      staleAfter: alertStaleAfter({
        refreshedAt: alertFreshnessAt(response.refreshedAt, response.checkedAt),
        fetchedAt: this.now(),
        cadenceMs: this.cadenceMs,
        computed: this.isComputed(response),
      }),
    };
    resolvers.get(id)!(entry);
    try {
      this.cache(id, entry);
    } catch (error) {
      console.warn(`[alerts] caching zone ${String(id)} failed; it will be re-read`, error);
    }
  }

  /** Per-zone reads for whatever the batch could not answer. */
  protected async fallback(ids: number[]): Promise<Map<number, ZoneAlertResponse<T>>> {
    // Share the server-aware limit used by the other per-zone alert reads.
    const results = await mapSettledWithLimit(ids, (id) => this.fetchSingular(id));
    return new Map(
      ids.map((id, index) => {
        const result = results[index];
        return [
          id,
          result.status === 'fulfilled' ? result.value : this.failure(this.failureMessage),
        ];
      }),
    );
  }

  /**
   * One batch request for `ids`, already chunked. Every id must appear either in
   * `results` or in `fallbackIds`; an id in neither is treated as missing.
   */
  protected abstract fetchChunk(ids: number[]): Promise<ChunkOutcome<T>>;

  /** The per-zone route, used for batch misses and rollback. */
  protected abstract fetchSingular(id: number): Promise<ZoneAlertResponse<T>>;

  protected abstract failure(message: string, status?: number): ApiResponse<T>;

  /** Message for a thrown request or a rejected per-zone read. */
  protected abstract get failureMessage(): string;

  /** Message for an id the batch answered about with nothing at all. */
  protected abstract get missingMessage(): string;
}
